import React, { useEffect, useState, useCallback, useMemo } from "react";
import Product from "../../home/Products/Product";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import NoProduct from "../../../assets/images/products/noproduct.jpg";
import ProductFilter from "./ProductFilter";

const AllProducts = ({
  itemsPerPage,
  selectedCategory,
  setSelectedCategory,
  productType, // Add productType prop
}) => {
  // State management
  const [itemOffset, setItemOffset] = useState(0);
  const [currentItems, setCurrentItems] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    sortBy: "",
    priceFrom: "",
    priceTo: "",
    rating: null,
  });

  // Applied filters (only updated when Apply button is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    sortBy: "",
    priceFrom: "",
    priceTo: "",
    rating: null,
  });

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Memoized function to check if current filters are different from applied filters
  const hasFilterChanges = useCallback(() => {
    return (
      filters.sortBy !== appliedFilters.sortBy ||
      filters.priceFrom !== appliedFilters.priceFrom ||
      filters.priceTo !== appliedFilters.priceTo ||
      filters.rating !== appliedFilters.rating
    );
  }, [filters, appliedFilters]);

  // Memoized function to fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: Math.floor(itemOffset / itemsPerPage) + 1, // Convert offset to page number
        limit: itemsPerPage,
        ...(selectedCategory?._id && { category: selectedCategory._id }),
        ...(productType && { productType }), // Add productType to filters
        // Update filter parameter names to match new API
        ...(appliedFilters.sortBy && { sortBy: appliedFilters.sortBy }),
        ...(appliedFilters.priceFrom && { minPrice: appliedFilters.priceFrom }),
        ...(appliedFilters.priceTo && { maxPrice: appliedFilters.priceTo }),
        ...(appliedFilters.rating && { minRating: appliedFilters.rating }),
      }).toString();

      const endpoint = `${apiPaths.getProducts}?${queryParams}`;
      const response = await ApiService({ method: "GET", endpoint });
      
      // Handle the new API response structure
      if (response.success) {
        const products = response.data.products || [];
        setCurrentItems(Array.isArray(products) ? products : []);
      } else {
        setCurrentItems([]);
      }
      setInitialLoad(false);
    } catch (err) {
      console.error("Error fetching products:", err);
      setInitialLoad(false);
    }
    setLoading(false);
  }, [appliedFilters, itemOffset, itemsPerPage, selectedCategory?._id, productType]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset pagination and filters when category changes
  useEffect(() => {
    setItemOffset(0);
    const resetValues = {
      sortBy: "",
      priceFrom: "",
      priceTo: "",
      rating: null,
    };
    setFilters(resetValues);
    setAppliedFilters(resetValues);
  }, [selectedCategory?._id]);

  // Memoized filter change handler
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Memoized apply filters function
  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters });
    setItemOffset(0); // Reset pagination
  }, [filters]);

  // Memoized reset filters function
  const resetFilters = useCallback(() => {
    const resetValues = {
      sortBy: "",
      priceFrom: "",
      priceTo: "",
      rating: null,
    };
    setFilters(resetValues);
    setAppliedFilters(resetValues);
    setItemOffset(0);
  }, []);

  // Memoized filtered products to prevent unnecessary re-renders
  const filteredProducts = useMemo(() => {
    return currentItems.filter(item => item.isActive === true);
  }, [currentItems]);

  // Memoized loading skeleton
  const loadingSkeleton = useMemo(() => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {[...Array(itemsPerPage)].map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm p-4 h-80 animate-pulse border border-gray-100"
        >
          <div className="w-full h-48 bg-gray-200 mb-4 rounded-md"></div>
          <div className="h-4 bg-gray-200 rounded-md mb-2 w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded-md w-1/2 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded-md w-1/3 mt-4"></div>
        </div>
      ))}
    </div>
  ), [itemsPerPage]);

  // Memoized no products component
  const noProductsComponent = useMemo(() => (
    <div className="w-full flex flex-col justify-center items-center py-16 bg-white rounded-lg shadow-sm">
      <img
        className="w-full h-full max-h-[200px] object-contain mb-6 opacity-70"
        src={NoProduct}
        alt="No product"
      />
      <h1 className="text-2xl font-semibold text-gray-800 mb-2">
        No Products Found
      </h1>
    </div>
  ), []);

  return (
    <div id="products-section" className="py-4">
      <div className="flex items-center justify-between mb-6">
        <h5 className="text-xl font-bold">
          Our Products
        </h5>
        <ProductFilter
          filters={filters}
          loading={loading}
          hasFilterChanges={hasFilterChanges}
          handleFilterChange={handleFilterChange}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          productType={productType}
        />
      </div>

      <div className="w-full">
        {/* Loading Skeleton */}
        {loading && loadingSkeleton}

        {/* Product List */}
        {!loading && (
          <>
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((item) => (
                  <div key={item._id} className="w-full">
                    <Product product={item} />
                  </div>
                ))}
              </div>
            ) : (
              noProductsComponent
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(AllProducts);